import { useState, type FC } from 'react';
import type { A2uiNodeViewProps } from '@/app/lib/a2ui/catalog.tsx';
import { str } from '@/app/lib/a2ui/props.ts';
import { ArrowUpRight, MessageCircle } from 'lucide-react';
import { formatVisitDate } from '../visit-slots.ts';
import {
  buildWhatsAppUrl,
  createReferenceId,
  formatIndianPhone,
} from '../whatsapp.ts';

interface SummaryRowProps {
  label: string;
  value: string;
  prominent?: boolean;
}

const SummaryRow: FC<SummaryRowProps> = ({ label, value, prominent = false }) => (
  <div className={`min-w-0 space-y-1 ${prominent ? 'rounded-xl bg-muted/70 p-3' : ''}`}>
    <dt className="text-base text-muted-foreground">{label}</dt>
    <dd className="break-words text-base font-medium text-foreground">{value}</dd>
  </div>
);

export const LakdiWhatsAppSummary: FC<A2uiNodeViewProps> = ({ node }) => {
  const mode = str(node.props.mode);
  const requestSummary = str(node.props.requestSummary);
  const customerName = str(node.props.customerName);
  const phone = str(node.props.phone);
  const area = str(node.props.area);
  const item = str(node.props.item);
  const size = str(node.props.size);
  const style = str(node.props.style);
  const material = str(node.props.material);
  const estimateRange = str(node.props.estimateRange);
  const visitType = str(node.props.visitType);
  const preferredDate = str(node.props.preferredDate);
  const preferredTime = str(node.props.preferredTime);
  const handoffReason = str(node.props.handoffReason);
  const [referenceId] = useState(() => createReferenceId());
  const isAppointment = mode === 'appointment';
  const dateLabel = preferredDate ? formatVisitDate(preferredDate) : '';
  const customerPhone = formatIndianPhone(phone);

  const messageLines = [
    isAppointment
      ? 'Hi Lakdi Studio, I would like to request this visit.'
      : 'Hi Lakdi Studio, I would like to speak with the team.',
    `Reference: ${referenceId}`,
    requestSummary ? `Request: ${requestSummary}` : '',
    item ? `Item: ${item}` : '',
    size ? `Size: ${size}` : '',
    style ? `Style: ${style}` : '',
    material ? `Material: ${material}` : '',
    estimateRange ? `Rough estimate: ${estimateRange}` : '',
    `Name: ${customerName}`,
    `Phone: ${customerPhone}`,
    `Area / pincode: ${area}`,
    visitType ? `Visit type: ${visitType}` : '',
    dateLabel ? `Preferred date: ${dateLabel} ${preferredDate.slice(0, 4)}` : '',
    preferredTime ? `Preferred time: ${preferredTime}` : '',
    handoffReason ? `Reason for handoff: ${handoffReason}` : '',
    isAppointment
      ? 'Please confirm this preferred slot on WhatsApp; I understand it is not reserved yet.'
      : '',
  ].filter(Boolean);
  const whatsappUrl = buildWhatsAppUrl(messageLines.join('\n'));
  const actionLabel = isAppointment ? 'Confirm on WhatsApp' : 'Talk to our team';

  return (
    <main className="@container/lakdi-summary mx-auto w-full max-w-container-2xl min-w-0">
      <article className="overflow-hidden rounded-3xl border border-border bg-card text-card-foreground shadow-elevated">
        <header className="bg-gradient-to-br from-card via-muted to-primary/10 p-5 @md/lakdi-summary:p-8">
          <p className="text-base font-semibold text-primary">Lakdi Studio</p>
          <h2 className="mt-2 font-display text-balance text-3xl font-semibold leading-tight tracking-tight text-foreground">
            {isAppointment ? 'Your visit request' : 'Talk to our team'}
          </h2>
          {requestSummary ? (
            <p className="mt-3 max-w-3xl break-words text-base leading-relaxed text-foreground">
              {requestSummary}
            </p>
          ) : null}
        </header>

        <section className="space-y-5 p-5 @md/lakdi-summary:p-8">
          <dl className="grid grid-cols-1 gap-x-8 gap-y-4 @md/lakdi-summary:grid-cols-2">
            {item ? <SummaryRow label="Item" value={item} /> : null}
            {size ? <SummaryRow label="Size" value={size} /> : null}
            {style ? <SummaryRow label="Style" value={style} /> : null}
            {material ? <SummaryRow label="Material tier" value={material} /> : null}
            {estimateRange ? <SummaryRow label="Rough estimate" value={estimateRange} prominent /> : null}
            <SummaryRow label="Name" value={customerName} />
            <SummaryRow label="Phone" value={customerPhone} />
            <SummaryRow label="Area / pincode" value={area} />
            {visitType ? <SummaryRow label="Visit type" value={visitType} /> : null}
            {dateLabel ? <SummaryRow label="Preferred date" value={dateLabel} /> : null}
            {preferredTime ? <SummaryRow label="Preferred time" value={preferredTime} /> : null}
            {handoffReason ? <SummaryRow label="Reason for handoff" value={handoffReason} /> : null}
            <SummaryRow label="Reference" value={referenceId} />
          </dl>

          {isAppointment ? (
            <p className="rounded-xl border border-border bg-muted/60 p-4 text-base leading-relaxed text-foreground">
              Your slot is a preference, not a reservation. The Lakdi Studio team will confirm it on WhatsApp.
            </p>
          ) : null}

          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${actionLabel} — opens a prefilled WhatsApp message`}
            className="inline-flex min-h-14 w-full touch-manipulation items-center justify-center gap-3 rounded-2xl bg-success px-5 py-4 text-center text-base font-semibold text-success-foreground shadow-sm transition-colors duration-150 hover:bg-success/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary motion-reduce:transition-none"
          >
            <MessageCircle className="h-5 w-5 shrink-0" aria-hidden="true" />
            <span>{actionLabel}</span>
            <ArrowUpRight className="h-5 w-5 shrink-0" aria-hidden="true" />
          </a>
          <p className="text-base leading-relaxed text-muted-foreground">
            WhatsApp opens with this message ready. Send it there to contact the team; opening the link alone does not send it.
          </p>
        </section>
      </article>
    </main>
  );
};
